import React, { useCallback, useEffect, useState } from 'react';
import {
    Check, Clock3, Pencil, Plus, Tag, Trash2, UserRound, X,
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import { onAdminRefresh } from '../../utils/adminSocket';
import Navbar from '../../components/Navbar';
import ConfirmDialog from '../../components/ConfirmDialog';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import './ServiceCategories.css';

// Backs the Category dropdown on business registration, the Add/Edit
// Service form, the Wedding Services browse filter, and the "Links To
// Category" selector on Service Cards — renaming or removing a category
// here takes effect in all of those immediately, the same way Profile
// Options works for matrimony fields.
const CATEGORY = 'servicecategory';

const ServiceCategories = () => {
    const [tab, setTab] = useState('categories'); // 'categories' | 'pending'
    const [options, setOptions] = useState([]);
    const [pending, setPending] = useState([]);
    const [newValue, setNewValue] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editingValue, setEditingValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);

    const fetchOptions = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const [activeRes, pendingRes] = await Promise.all([
                API.get('/admin/options', { params: { category: CATEGORY, status: 'active' } }),
                API.get('/admin/options', { params: { category: CATEGORY, status: 'pending' } }),
            ]);
            setOptions(activeRes.data.options || []);
            setPending(pendingRes.data.options || []);
        } catch (err) {
            toast.error('Failed to load categories');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchOptions(); }, [fetchOptions]);

    // realtime: a category suggested by a provider appears here at once
    useEffect(() => onAdminRefresh(() => fetchOptions(true)), [fetchOptions]);

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!newValue.trim()) return;
        try {
            await API.post('/admin/options', { category: CATEGORY, value: newValue.trim() });
            toast.success(`Added "${newValue.trim()}"`);
            setNewValue('');
            fetchOptions();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to add');
        }
    };

    const handleDelete = (id, value) => setPendingDelete({ id, value });

    const confirmDeleteCategory = async () => {
        const target = pendingDelete;
        if (!target) return;
        setPendingDelete(null);
        try {
            await API.delete(`/admin/options/${target.id}`);
            toast.success('Removed');
            fetchOptions();
        } catch (err) {
            toast.error('Failed to remove');
        }
    };

    const startEdit = (o) => { setEditingId(o._id); setEditingValue(o.value); };
    const cancelEdit = () => { setEditingId(null); setEditingValue(''); };

    const handleSaveRename = async (id) => {
        if (!editingValue.trim()) return;
        try {
            await API.put(`/admin/options/${id}`, { value: editingValue.trim() });
            toast.success('Renamed — this takes effect everywhere immediately');
            setEditingId(null);
            setEditingValue('');
            fetchOptions();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to rename');
        }
    };

    const handleApprove = async (o) => {
        try {
            await API.put(`/admin/options/${o._id}/approve`);
            toast.success(`"${o.value}" is now a live category`);
            fetchOptions();
        } catch (err) {
            toast.error('Failed to approve');
        }
    };

    const handleReject = async (o) => {
        try {
            await API.delete(`/admin/options/${o._id}`);
            toast.success('Suggestion rejected');
            fetchOptions();
        } catch (err) {
            toast.error('Failed to reject');
        }
    };

    const TABS = [
        { id: 'categories', label: 'Categories', Icon: Tag, count: options.length },
        { id: 'pending', label: 'Pending Suggestions', Icon: Clock3, count: pending.length },
    ];

    return (
        <div className="servicecategories-layout">
            <Sidebar />
            <main className="admin-content servicecategories-content">
                <Navbar title={<span className="servicecategories-title"><Tag size={19} /> Service Categories</span>} />
                <div className="servicecategories-page">
                    <div className="servicecategories-heading">
                        <div>
                            <h2>Service categories</h2>
                            <p>
                                Manage the categories vendors pick from when listing a service, and that appear in the
                                Wedding Services filter. Renaming a category here updates it everywhere at once —
                                merging two categories is just renaming one to match the other's exact name, then
                                removing the now-empty duplicate.
                            </p>
                        </div>
                    </div>

                    <section className="servicecategories-stats" aria-label="Category overview">
                        <article className="servicecategories-stat-card">
                            <span className="servicecategories-stat-icon primary"><Tag size={19} /></span>
                            <div><strong>{options.length}</strong><span>Live categories</span></div>
                        </article>
                        <article className="servicecategories-stat-card">
                            <span className="servicecategories-stat-icon amber"><Clock3 size={19} /></span>
                            <div><strong>{pending.length}</strong><span>Pending suggestions</span></div>
                        </article>
                    </section>

                    <section className="servicecategories-panel">
                        <div className="servicecategories-toolbar">
                            <div className="servicecategories-tabs" aria-label="Switch view">
                                {TABS.map(({ id, label, Icon, count }) => (
                                    <button key={id} type="button" className={tab === id ? 'active' : ''} onClick={() => setTab(id)}>
                                        <Icon size={14} />{label}{count > 0 && <span>{count}</span>}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {tab === 'categories' && (
                            <div className="servicecategories-body">
                                <form onSubmit={handleAdd} className="servicecategories-add-row">
                                    <input placeholder="Add a new category..." value={newValue} onChange={e => setNewValue(e.target.value)} />
                                    <button type="submit"><Plus size={15} /> Add</button>
                                </form>

                                {loading ? (
                                    <div className="servicecategories-state"><span className="servicecategories-spinner" />Loading...</div>
                                ) : options.length === 0 ? (
                                    <div className="servicecategories-empty"><Tag size={26} /><strong>No categories yet</strong><span>Add the first one above.</span></div>
                                ) : (
                                    <div className="servicecategories-chips">
                                        {options.map(o => (
                                            <div key={o._id} className="servicecategories-chip">
                                                {editingId === o._id ? (
                                                    <>
                                                        <input
                                                            autoFocus
                                                            className="servicecategories-chip-input"
                                                            value={editingValue}
                                                            onChange={e => setEditingValue(e.target.value)}
                                                            onKeyDown={e => { if (e.key === 'Enter') handleSaveRename(o._id); if (e.key === 'Escape') cancelEdit(); }}
                                                        />
                                                        <button type="button" className="save" onClick={() => handleSaveRename(o._id)} title="Save"><Check size={13} /></button>
                                                        <button type="button" onClick={cancelEdit} title="Cancel"><X size={13} /></button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span>{o.value}</span>
                                                        <button type="button" onClick={() => startEdit(o)} title="Rename"><Pencil size={13} /></button>
                                                        <button type="button" className="danger" onClick={() => handleDelete(o._id, o.value)} title="Remove"><Trash2 size={13} /></button>
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {tab === 'pending' && (
                            <div className="servicecategories-body">
                                {loading ? (
                                    <div className="servicecategories-state"><span className="servicecategories-spinner" />Loading...</div>
                                ) : pending.length === 0 ? (
                                    <div className="servicecategories-empty">
                                        <Clock3 size={26} />
                                        <strong>No pending suggestions</strong>
                                        <span>New categories typed in by a service provider during registration land here for review.</span>
                                    </div>
                                ) : (
                                    <div className="servicecategories-pending-list">
                                        {pending.map(o => (
                                            <div key={o._id} className="servicecategories-pending-row">
                                                <span className="servicecategories-pending-icon"><UserRound size={15} /></span>
                                                <div className="servicecategories-pending-info">
                                                    <strong>{o.value}</strong>
                                                    <span>Suggested by {o.suggestedByName || 'a service provider'}</span>
                                                </div>
                                                <div className="servicecategories-pending-actions">
                                                    <button type="button" className="approve" onClick={() => handleApprove(o)}><Check size={14} />Approve</button>
                                                    <button type="button" className="reject" onClick={() => handleReject(o)}><X size={14} />Reject</button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </section>
                </div>
            </main>

            <ConfirmDialog
                open={Boolean(pendingDelete)}
                title="Remove Category?"
                message={pendingDelete ? `Remove "${pendingDelete.value}"?` : ''}
                items={['Listings that already use this category keep it', 'This only removes it from dropdowns going forward']}
                confirmLabel="Remove"
                danger
                onConfirm={confirmDeleteCategory}
                onCancel={() => setPendingDelete(null)}
            />
        </div>
    );
};

export default ServiceCategories;
